import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { NEUTRAL, applyIdentity, loadIdentity, readCache } from './identity';

/**
 * Makes the tenant's identity available to the app.
 *
 * Copied verbatim into each frontend alongside identity.js. The one per-app
 * detail - which backend to ask - is passed in as a prop rather than imported,
 * so this file stays identical everywhere.
 *
 * Renders children immediately rather than blocking on the request. A cached
 * identity is applied synchronously first, so a reload paints the right colours
 * on the first frame instead of flashing neutral; the fetch then refreshes it.
 */

const IdentityContext = createContext(NEUTRAL);

export function IdentityProvider({ baseUrl, children }) {
  const [identity, setIdentity] = useState(() => {
    const cached = readCache();
    if (cached) applyIdentity(cached);
    return cached || NEUTRAL;
  });

  useEffect(() => {
    let cancelled = false;

    loadIdentity(baseUrl).then((next) => {
      if (cancelled) return;
      setIdentity(next);
      applyIdentity(next);
    });

    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  const value = useMemo(() => identity, [identity]);

  return <IdentityContext.Provider value={value}>{children}</IdentityContext.Provider>;
}

/**
 * The current school. Always returns an object, so callers never null-check:
 * before identity resolves, and if it never does, this is NEUTRAL - empty name,
 * empty logo, neutral grey.
 */
export function useIdentity() {
  return useContext(IdentityContext);
}

/**
 * The school's logo, or nothing at all.
 *
 * Deliberately renders nothing when there is no logo rather than falling back
 * to a bundled image. A placeholder here is how one client's crest ends up on
 * another client's page.
 */
export function SchoolLogo({ className = '', alt }) {
  const identity = useIdentity();

  if (!identity.logoUrl) {
    return identity.shortName
      ? <span className={className} style={{ fontWeight: 700 }}>{identity.shortName}</span>
      : null;
  }

  return (
    <img
      src={identity.logoUrl}
      alt={alt || identity.name || 'School logo'}
      className={className}
    />
  );
}
